import React, { useState, useEffect, useMemo } from 'react';
import { lookupsApi, backtestApi } from '../../services/api';
import { notificationService } from '../../services/notificationService';
import { Backtest, TradeDetail } from '../../types';
import Dropdown from '../../components/common/Dropdown';
import Button from '../../components/common/Button';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import TradingChart from '../../components/TradingChart/TradingChart';
import TradeHistory from '../../components/TradeHistory/TradeHistory';
import BacktestAnalysis from '../../components/BacktestAnalysis/BacktestAnalysis';
import AIInsights from '../../components/AIInsights/AIInsights';
import './LoadBacktest.css';

interface CandleData {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
}

const LoadBacktest: React.FC = () => {
  const [backtests, setBacktests] = useState<Backtest[]>([]);
  const [selectedBacktest, setSelectedBacktest] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'chart' | 'trades' | 'analysis' | 'ai'>('chart');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Results state
  const [results, setResults] = useState<any | null>(null);
  const [trades, setTrades] = useState<TradeDetail[]>([]);
  const [candles, setCandles] = useState<CandleData[]>([]);
  const [analysisData, setAnalysisData] = useState<any>(null);
  const [backtestId, setBacktestId] = useState<number | null>(null);
  const [aiInsights, setAiInsights] = useState<{ insights: string; strategyName: string } | null>(null);

  useEffect(() => {
    loadBacktests();
  }, []);

  const loadBacktests = async () => {
    try {
      const backs = await lookupsApi.getBacktests();
      setBacktests(backs);
    } catch (error) {
      console.error('Error loading backtests:', error);
    }
  };

  const backtestOptions = useMemo(() => {
    return backtests.map(b => ({
      value: b.backtest_id,
      label: `#${b.backtest_id} - ${b.epic} ${b.resolution} (${b.total_trades} trades, ${(b.win_rate * 100).toFixed(1)}% win rate)`
    }));
  }, [backtests]);

  const formatDateRange = () => {
    if (!results) return '';
    const startDate = results.start_date || results.startDate;
    const endDate = results.end_date || results.endDate;

    if (!startDate || !endDate) return '';

    const formatDate = (dateStr: string) => {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    return ` (${formatDate(startDate)} - ${formatDate(endDate)})`;
  };

  const handleSelectBacktest = async () => {
    if (!selectedBacktest) {
      notificationService.warning('Please select a backtest');
      return;
    }

    setLoading(true);
    try {
      const backtest = backtests.find(b => b.backtest_id === Number(selectedBacktest));
      const fromDate = backtest?.from_date || backtest?.fromDate;
      const toDate = backtest?.to_date || backtest?.toDate;

      // Fetch all data
      const [tradesData, analysisData, aiInsightsData] = await Promise.all([
        backtestApi.getBacktestTrades(Number(selectedBacktest)),
        backtestApi.analyzeBacktestResults(Number(selectedBacktest)),
        backtestApi.getAIInsights(Number(selectedBacktest))
      ]);

      setTrades(tradesData.trades);

      const completeResults = {
        ...analysisData,
        backtestId: tradesData.backtest_id || (tradesData as any).backtestId || Number(selectedBacktest),
        totalTrades: tradesData.total_trades || (tradesData as any).totalTrades || tradesData.trades.length,
        trades: tradesData.trades,
        start_date: fromDate,
        startDate: fromDate,
        end_date: toDate,
        endDate: toDate
      };

      setResults(completeResults);
      setAnalysisData(analysisData);
      setBacktestId(Number(selectedBacktest));
      setAiInsights(aiInsightsData);

      // Fetch chart data
      try {
        const chartData = await backtestApi.getChartData(Number(selectedBacktest));
        if (chartData.candles && chartData.candles.length > 0) {
          setCandles(chartData.candles);
        }
      } catch (chartError) {
        console.error('Error fetching chart data:', chartError);
      }
    } catch (error) {
      console.error('Error loading backtest:', error);
      notificationService.error('Failed to load backtest. Check console for details.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = () => {
    if (!selectedBacktest) {
      notificationService.warning('Please select a backtest to delete');
      return;
    }
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteDialog(false);
    setLoading(true);
    try {
      await backtestApi.deleteBacktest(Number(selectedBacktest));
      notificationService.success('Backtest deleted successfully');

      // Clear results and reload list
      setSelectedBacktest('');
      setResults(null);
      setTrades([]);
      setCandles([]);
      setAnalysisData(null);
      setBacktestId(null);
      setAiInsights(null);

      await loadBacktests();
    } catch (error: any) {
      console.error('Error deleting backtest:', error);
      notificationService.error(`Failed to delete backtest: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="load-backtest-page">
      <div className="page-header">
        <h2>Load Existing Backtest</h2>
        <p className="page-description">Select a backtest to view its results, analyze performance, and get AI insights</p>
      </div>

      <div className="backtest-selector-card">
        <div className="selector-content">
          <Dropdown
            label="Select Backtest"
            value={selectedBacktest}
            onChange={setSelectedBacktest}
            options={backtestOptions}
            placeholder="Choose a backtest from the list"
          />

          <div className="action-buttons">
            <Button
              onClick={handleSelectBacktest}
              loading={loading}
              disabled={loading || !selectedBacktest}
            >
              Load Backtest
            </Button>

            <Button
              onClick={handleDeleteClick}
              variant="secondary"
              loading={loading}
              disabled={loading || !selectedBacktest}
              className="delete-btn"
            >
              Delete
            </Button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Delete Backtest"
        message={`Are you sure you want to delete backtest #${selectedBacktest}? This action cannot be undone and all associated data will be permanently removed.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowDeleteDialog(false)}
      />

      {(trades.length > 0 || candles.length > 0) && (
        <div className="results-section">
          <div className="results-tabs-header">
            <button
              className={`results-tab-button ${activeTab === 'chart' ? 'active' : ''}`}
              onClick={() => setActiveTab('chart')}
            >
              Chart View
            </button>
            <button
              className={`results-tab-button ${activeTab === 'trades' ? 'active' : ''}`}
              onClick={() => setActiveTab('trades')}
            >
              Trade History
            </button>
            <button
              className={`results-tab-button ${activeTab === 'analysis' ? 'active' : ''}`}
              onClick={() => setActiveTab('analysis')}
            >
              Analysis
            </button>
            <button
              className={`results-tab-button ${activeTab === 'ai' ? 'active' : ''}`}
              onClick={() => setActiveTab('ai')}
            >
              AI Insights
            </button>
          </div>

          <div className="results-tab-content">
            {activeTab === 'chart' && (
              <TradingChart trades={trades} candles={candles} dateRange={formatDateRange()} />
            )}
            {activeTab === 'trades' && (
              <TradeHistory trades={trades} backtestId={backtestId || undefined} dateRange={formatDateRange()} />
            )}
            {activeTab === 'analysis' && (
              <BacktestAnalysis analysisData={analysisData} trades={trades} dateRange={formatDateRange()} />
            )}
            {activeTab === 'ai' && (
              <AIInsights
                backtestId={backtestId}
                cachedInsights={aiInsights}
                onInsightsFetched={setAiInsights}
                dateRange={formatDateRange()}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoadBacktest;
